import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import robustFetch from "@/utils/robustFetch";

interface AggregateRatingProps {
  className?: string;
}

interface RatingData {
  averageRating: number;
  totalReviews: number;
}

export default function AggregateRating({
  className = "",
}: AggregateRatingProps) {
  const [ratingData, setRatingData] = useState<RatingData>({
    averageRating: 4.8,
    totalReviews: 638,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRating = async () => {
      try {
        // Fetch total aggregate reviews across ALL conversion types
        const response = await robustFetch("/api/reviews/stats/total", {
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
        });

        // Check if response is ok
        if (!response.ok) {
          console.warn(
            `⚠️ API returned ${response.status} for aggregate rating`,
          );
          setIsLoading(false);
          return;
        }

        // Get response text first to check if it's empty
        const text = await response.text();

        if (!text || text.trim().length === 0) {
          console.warn("⚠️ API returned empty response for aggregate rating");
          setIsLoading(false);
          return;
        }

        // Try to parse JSON
        let data;
        try {
          data = JSON.parse(text);
        } catch (jsonError) {
          console.error(
            "❌ Failed to parse JSON response:",
            text.substring(0, 100),
          );
          setIsLoading(false);
          return;
        }

        if (data.success && data.data) {
          setRatingData({
            averageRating: data.data.averageRating || 5.0,
            totalReviews: data.data.totalReviews || 1,
          });
          console.log("✅ Aggregate rating loaded:", data.data);
        } else {
          console.warn(
            "⚠️ API returned success=false for aggregate rating:",
            data,
          );
        }
      } catch (error) {
        console.error("❌ Failed to fetch aggregate rating:", error);
        // Keep default values (5.0 rating, 1 review)
      } finally {
        setIsLoading(false);
      }
    };

    fetchRating();
  }, []);

  const fullStars = Math.floor(ratingData.averageRating);
  const hasHalfStar = ratingData.averageRating % 1 >= 0.5;

  return (
    <div
      className={`inline-flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 md:gap-3 px-2 ${className}`}
    >
      {/* Label for SEO and user clarity */}
      <span className="text-[10px] xs:text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">
        Overall conversion quality rating
      </span>

      {/* Visual rating display - structured data is provided via JSON-LD in page head */}
      <div className="flex items-center gap-0.5 sm:gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-3.5 h-3.5 xs:w-4 xs:h-4 sm:w-5 sm:h-5 ${
              i < fullStars
                ? "text-yellow-400 fill-yellow-400"
                : i === fullStars && hasHalfStar
                  ? "text-yellow-400 fill-yellow-400/50"
                  : "text-gray-300 fill-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-xs xs:text-sm sm:text-base font-semibold text-gray-700 whitespace-nowrap">
        {ratingData.averageRating.toFixed(1)}
        <span className="text-gray-500 font-normal"> / 5.0</span>
      </span>
      <span className="text-[10px] xs:text-xs sm:text-sm text-gray-600 whitespace-nowrap">
        ({ratingData.totalReviews.toLocaleString()}{" "}
        {ratingData.totalReviews === 1 ? "review" : "reviews"})
      </span>
    </div>
  );
}
